import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { DataGrid } from "@mui/x-data-grid";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Close from "../../assets/images/close-icon.png";
import Typography from "@mui/material/Typography";
import "../../components/common/css/datatable.scss";
import Box from "@mui/material/Box";
import { useDispatch } from "react-redux";
import { usePermissions } from "../../context/permissionContext";
import { validatePermission } from "../../utils/permissionUtils";
import Loader from "../../components/Loader/Loader";
import { useForm } from "react-hook-form";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PageShell from "../../components/layout/PageShell";
import PageHead from "../../components/layout/PageHead";

const ManageRole = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState({
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });
  const [query, setQuery] = useState("");

  const token = localStorage.getItem("accessToken");
  const dispatch = useDispatch();
  const { permissions, permissionsMapping } = usePermissions();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const hasEditPermission = validatePermission(
    "Users Management - Manage System Role:Edit",
    permissions,
    permissionsMapping
  );

  const hasDeletePermission = validatePermission(
    "Users Management - Manage System Role:Delete",
    permissions,
    permissionsMapping
  );

  const userColumns = [
    {
      field: "serial",
      headerName: "S.No",
      flex: 1,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "role", headerName: "Role", width: 250 },
    { field: "name", headerName: "Name", width: 250 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <div className="cellAction">
          {hasEditPermission && (
            <div className="editButton" onClick={() => handleEdit(params.row)}>
              <EditOutlinedIcon />
            </div>
          )}
          {hasDeletePermission && (
            <div
              className="deleteButton"
              onClick={() => deleteFunction(params.row.id)}
            >
              <DeleteOutlineIcon />
            </div>
          )}
        </div>
      ),
    },
  ];

  const handleEdit = (row) => {
    setSelectedRole(row);
    reset();
    setValue("role", row.role);
    setValue("name", row.name);
    setOpenEdit(true);
  };

  const fetchRolesData = useCallback(() => {
    setLoading(true);
    axios
      .post(
        process.env.REACT_APP_API + "get-role",
        { page: pageState.page, limit: pageState.pageSize },
        { headers: { "x-access-token": token } }
      )
      .then((response) => {
        setPageState((old) => ({
          ...old,
          data: response.data.data.rows,
          total: response.data.data.count,
        }));
      })
      .catch((error) => {
        Swal.fire({
          title: error?.response?.data?.message || "Failed to load roles",
          icon: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [pageState.page, pageState.pageSize, token]);

  useEffect(() => {
    fetchRolesData();
  }, [fetchRolesData, query]);

  const deleteFunction = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this Role!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios
          .patch(
            process.env.REACT_APP_API + "delete-role",
            { role_id: id },
            { headers: { "x-access-token": token } }
          )
          .then(() => {
            Swal.fire(
              "Deleted!",
              "System Role deleted successfully.",
              "success"
            );
            fetchRolesData();
          })
          .catch((error) => {
            Swal.fire(
              "Error",
              error.response?.data?.message || "Failed to delete.",
              "error"
            );
          })
          .finally(() => setLoading(false));
      }
    });
  };

  const onSubmitAdd = (data) => {
    setLoading(true);
    axios
      .post(process.env.REACT_APP_API + "save-role", data, {
        headers: { "x-access-token": token },
      })
      .then(() => {
        Swal.fire("Success", "Role Added Successfully", "success");
        setOpenAdd(false);
        reset();
        fetchRolesData();
      })
      .catch((error) => {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Something went wrong",
          "error"
        );
      })
      .finally(() => setLoading(false));
  };

  const onSubmitEdit = (data) => {
    setLoading(true);
    axios
      .patch(
        process.env.REACT_APP_API + "update-role",
        { ...data, role_id: selectedRole?.id },
        { headers: { "x-access-token": token } }
      )
      .then(() => {
        Swal.fire("Success", "System Role Updated Successfully", "success");
        setOpenEdit(false);
        setSelectedRole(null);
        reset();
        fetchRolesData();
      })
      .catch((error) => {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Something went wrong",
          "error"
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <PageShell><PageHead title="System roles" /><div className="list">
      <div className="listContainer">
        <div className="datatable">
          <Loader isLoading={loading} />
          <div className="datatableTitle manageUser-top">
            <h5 className="h5Title" style={{ textAlign: "center" }}>
              Manage System Role
            </h5>
            {hasEditPermission && (
              <button
                className="link"
                onClick={() => {
                  reset(); 
                  setOpenAdd(true);
                }}
                style={{ background: "transparent" }}
              >
                Add New
              </button>
            )}
          </div>
          <DataGrid
            className="datagrid"
            autoHeight
            rows={pageState.data}
            rowCount={pageState.total}
            rowsPerPageOptions={[10, 30, 50, 70, 100]}
            pagination
            page={pageState.page - 1}
            pageSize={pageState.pageSize}
            paginationMode="server"
            onPageChange={(newPage) =>
              setPageState((old) => ({ ...old, page: newPage + 1 }))
            }
            onPageSizeChange={(newPageSize) =>
              setPageState((old) => ({ ...old, pageSize: newPageSize }))
            }
            columns={userColumns}
          />
        </div>

        {/* Add Modal */}
        <Modal open={openAdd} onClose={() => setOpenAdd(false)}>
          <Box className="modal-popup">
            <div className="modal-top">
              <Typography variant="h6">New Role Entry</Typography>
              <Button className="closeBtn" onClick={() => setOpenAdd(false)}>
                <img src={Close} alt="close-icon" />
              </Button>
            </div>
            <Typography id="modal-modal-description" sx={{ mt: 1 }}>
              <div className="new">
                <div className="newContainer">
                  <div
                    className="bottom"
                    style={{ boxShadow: "none", padding: "0" }}
                  >
                    <div className="right">
                      <form
                        onSubmit={handleSubmit(onSubmitAdd)}
                        className="accredited-investorForm"
                      >
                        <div className="formInput" id="commissionInput">
                          <label>Role</label>
                          <input
                            type="text"
                            className="custome-input"
                            {...register("role", {
                              required: "Role is required",
                              pattern: {
                                value: /^[a-zA-Z_ ]*$/,
                                message: "Only letters allowed",
                              },
                            })}
                          />
                          {errors.role && (
                            <span className="invalid-feedback">
                              {errors.role.message}
                            </span>
                          )}
                        </div>
                        <div className="formInput" id="commissionInput">
                          <label>Name</label>
                          <input
                            type="text"
                            className="custome-input"
                            {...register("name", {
                              required: "Name is required",
                              pattern: {
                                value: /^[a-zA-Z_ ]*$/,
                                message: "Only letters allowed",
                              },
                            })}
                          />
                          {errors.name && (
                            <span className="invalid-feedback">
                              {errors.name.message}
                            </span>
                          )}
                        </div>
                        <div className="submitButton">
                          <button className="approveButton" type="submit">
                            Add
                          </button>
                          <button
                            className="cancelButton"
                            type="button"
                            onClick={() => setOpenAdd(false)}
                          >
                            Close
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </Typography>
          </Box>
        </Modal>

        {/* Edit Modal */}
        <Modal open={openEdit} onClose={() => setOpenEdit(false)}>
          <Box className="modal-popup">
            <div className="modal-top">
              <Typography variant="h6">Update System Role</Typography>
              <Button className="closeBtn" onClick={() => setOpenEdit(false)}>
                <img src={Close} alt="close-icon" />
              </Button>
            </div>
            <Typography id="modal-modal-description" sx={{ mt: 1 }}>
              <div className="new">
                <div className="newContainer">
                  <div
                    className="bottom"
                    style={{ boxShadow: "none", padding: "0" }}
                  >
                    <div className="right">
                      <form
                        onSubmit={handleSubmit(onSubmitEdit)}
                        className="accredited-investorForm"
                      >
                        <div className="formInput" id="commissionInput">
                          <label>Role</label>
                          <input
                            type="text"
                            className="custome-input"
                            {...register("role", {
                              required: "Role is required",
                              pattern: {
                                value: /^[a-zA-Z_ ]*$/,
                                message: "Only letters allowed",
                              },
                            })}
                          />
                          {errors.role && (
                            <span className="invalid-feedback">
                              {errors.role.message}
                            </span>
                          )}
                        </div>
                        <div className="formInput" id="commissionInput">
                          <label>Name</label>
                          <input
                            type="text"
                            className="custome-input"
                            {...register("name", {
                              required: "Name is required",
                              pattern: {
                                value: /^[a-zA-Z_ ]*$/,
                                message: "Only letters allowed",
                              },
                            })}
                          />
                          {errors.name && (
                            <span className="invalid-feedback">
                              {errors.name.message}
                            </span>
                          )}
                        </div>
                        <div className="submitButton">
                          <button className="approveButton" type="submit">
                            Update
                          </button>
                          <button
                            className="cancelButton"
                            type="button"
                            onClick={() => setOpenEdit(false)}
                          >
                            Close
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </Typography>
          </Box>
        </Modal>
      </div>
    </div></PageShell>
  );
};

export default ManageRole;
